import { useCallback, useEffect, useRef, useState } from "react";
import { getPaymentsErrorMessage } from "../terminal-payment/terminalPaymentApi";
import {
    fetchLatestMbwayRequest,
    fetchResponsibleRelativePhone,
    isOpenMbwayRequest,
    MbwayRequest,
    replaceMbwayRequest,
    startMbwayRequest,
} from "./mbwayPaymentApi";
import { SplitPayment } from "../split-payment/splitPayments";
import { INVALID_MBWAY_PHONE_MESSAGE, normalizePortugueseMobile } from "./mbwayPhone";

const POLL_INTERVAL_MS = 2000;
const COUNTDOWN_TICK_MS = 1000;

export type MbwayPaymentScreen = "loading" | "phone" | "waiting" | "result";

type MbwayPaymentScreenInput = {
    isLoading: boolean;
    isPhoneFormOpen: boolean;
    request: MbwayRequest | null;
};

const resolveScreen = ({ isLoading, isPhoneFormOpen, request }: MbwayPaymentScreenInput) => {
    if (isLoading) return "loading";
    if (isPhoneFormOpen || request === null) return "phone";
    const screen: MbwayPaymentScreen = isOpenMbwayRequest(request) ? "waiting" : "result";
    return screen;
};

const readResponsibleRelativeMobile = async (treatmentId: number | null) => {
    const relativePhone = treatmentId ? await fetchResponsibleRelativePhone(treatmentId).catch(() => null) : null;
    const relativeMobile = normalizePortugueseMobile(relativePhone);
    return relativeMobile;
};

export const resolvePrefilledPhone = async (clientPhone: string | null, treatmentId: number | null) => {
    const prefilledPhone = normalizePortugueseMobile(clientPhone) ?? (await readResponsibleRelativeMobile(treatmentId)) ?? "";
    return prefilledPhone;
};

export const useMbwayPayment = (orderId: number, clientPhone: string | null, treatmentId: number | null, onPaid: () => void) => {
    const [request, setRequest] = useState<MbwayRequest | null>(null);
    const [phone, setPhone] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isReplacing, setIsReplacing] = useState(false);
    const [isPhoneFormOpen, setIsPhoneFormOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const notifiedPaidRequestId = useRef<number | null>(null);

    useEffect(() => {
        let isActive = true;
        const latestRequestLoad = fetchLatestMbwayRequest(orderId).catch((error) => {
            if (isActive) setErrorMessage(getPaymentsErrorMessage(error));
            return null;
        });
        Promise.all([latestRequestLoad, resolvePrefilledPhone(clientPhone, treatmentId)])
            .then(([latestRequest, prefilledPhone]) => {
                if (!isActive) return;
                setRequest(latestRequest);
                setPhone(prefilledPhone);
            })
            .finally(() => isActive && setIsLoading(false));
        return () => {
            isActive = false;
        };
    }, [orderId, clientPhone, treatmentId]);

    useEffect(() => {
        if (!isOpenMbwayRequest(request) || isReplacing) return;
        let isActive = true;
        const pollTimer = setTimeout(() => {
            fetchLatestMbwayRequest(orderId)
                .then((latestRequest) => isActive && setRequest(latestRequest))
                .catch(() => isActive && setRequest((currentRequest) => (currentRequest ? { ...currentRequest } : currentRequest)));
        }, POLL_INTERVAL_MS);
        return () => {
            isActive = false;
            clearTimeout(pollTimer);
        };
    }, [request, isReplacing, orderId]);

    const isRequestOpen = isOpenMbwayRequest(request);
    const serverSecondsRemaining = request?.seconds_remaining ?? 0;

    useEffect(() => {
        setSecondsRemaining(serverSecondsRemaining);
        if (!isRequestOpen) return;
        const countdownTimer = setInterval(() => {
            setSecondsRemaining((currentSeconds) => Math.max(0, currentSeconds - 1));
        }, COUNTDOWN_TICK_MS);
        return () => clearInterval(countdownTimer);
    }, [isRequestOpen, serverSecondsRemaining]);

    useEffect(() => {
        const isRecordedPayment = request?.status === "paid" && request.recording_status === "recorded";
        if (!request || !isRecordedPayment || notifiedPaidRequestId.current === request.id) return;
        notifiedPaidRequestId.current = request.id;
        onPaid();
    }, [request, onPaid]);

    const sendRequest = useCallback(async (splitPayments: Array<SplitPayment>) => {
        const mobile = normalizePortugueseMobile(phone);
        if (!mobile) {
            setErrorMessage(INVALID_MBWAY_PHONE_MESSAGE);
            return;
        }
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            const startedRequest = await startMbwayRequest(orderId, mobile, splitPayments);
            setRequest(startedRequest);
            setIsPhoneFormOpen(false);
        } catch (error) {
            const runningRequest = await fetchLatestMbwayRequest(orderId).catch(() => null);
            if (isOpenMbwayRequest(runningRequest)) {
                setRequest(runningRequest);
                setIsPhoneFormOpen(false);
            } else {
                setErrorMessage(getPaymentsErrorMessage(error));
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [orderId, phone]);

    const replaceRequest = useCallback(async () => {
        if (!request) return;
        setIsReplacing(true);
        setErrorMessage(null);
        try {
            const replacedRequest = await replaceMbwayRequest(request.id);
            setRequest(replacedRequest);
            setIsPhoneFormOpen(replacedRequest.status === "replaced");
        } catch (error) {
            setErrorMessage(getPaymentsErrorMessage(error));
        } finally {
            setIsReplacing(false);
        }
    }, [request]);

    const openPhoneForm = useCallback(() => {
        setErrorMessage(null);
        setIsPhoneFormOpen(true);
    }, []);

    const changePhone = useCallback((nextPhone: string) => {
        setErrorMessage(null);
        setPhone(nextPhone);
    }, []);

    const screen = resolveScreen({ isLoading, isPhoneFormOpen, request });
    const mbwayPayment = {
        screen,
        request,
        phone,
        secondsRemaining,
        isSubmitting,
        isReplacing,
        errorMessage,
        sendRequest,
        replaceRequest,
        openPhoneForm,
        changePhone,
    };
    return mbwayPayment;
};
